import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, InputNumber, Select, Space, Typography,
  message, Popconfirm, Tag, Tabs, Row, Col, List, DatePicker,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined,
  SwapOutlined, ReloadOutlined,
} from '@ant-design/icons';
import api from '../api/client';

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

interface Department {
  id: number;
  name: string;
  sort_order: number;
  employee_count: number;
}

interface Position {
  id: number;
  name: string;
  sort_order: number;
  department_ids: number[];
  department_names: string[];
  employee_count: number;
}

interface DeptEmployee {
  id: number;
  last_name: string;
  first_name: string;
  middle_name: string | null;
  birth_date: string | null;
  department_id: number;
  department_name: string | null;
}

// ==================== DEPARTMENTS TAB ====================

const DepartmentsTab: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDept, setEditingDept] = useState<Department | null>(null);
  const [form] = Form.useForm();

  // Rename state
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameDept, setRenameDept] = useState<Department | null>(null);
  const [renameEmployees, setRenameEmployees] = useState<DeptEmployee[]>([]);
  const [newName, setNewName] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);

  // Move employees state
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveDept, setMoveDept] = useState<Department | null>(null);
  const [moveEmployees, setMoveEmployees] = useState<(DeptEmployee & { newDepartmentId?: number })[]>([]);
  const [moveLoading, setMoveLoading] = useState(false);

  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/departments');
      setDepartments(res.data);
    } catch {
      message.error('Ошибка загрузки подразделений');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDepartments(); }, [fetchDepartments]);

  const openCreateModal = () => {
    setEditingDept(null);
    form.resetFields();
    form.setFieldValue('sort_order', departments.length + 1);
    setModalOpen(true);
  };

  const handleEdit = async (dept: Department) => {
    // If has employees, show rename modal with employee list
    if (dept.employee_count > 0) {
      setRenameDept(dept);
      setNewName(dept.name);
      setRenameLoading(true);
      setRenameModalOpen(true);
      try {
        const res = await api.get(`/departments/${dept.id}/employees`);
        setRenameEmployees(res.data);
      } catch {
        setRenameEmployees([]);
      } finally {
        setRenameLoading(false);
      }
    } else {
      setEditingDept(dept);
      form.setFieldsValue({ name: dept.name, sort_order: dept.sort_order });
      setModalOpen(true);
    }
  };

  const handleRenameConfirm = async () => {
    if (!renameDept || !newName.trim()) return;
    try {
      await api.put(`/departments/${renameDept.id}`, { name: newName.trim() });
      message.success('Подразделение переименовано');
      setRenameModalOpen(false);
      fetchDepartments();
    } catch (err: any) {
      message.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingDept) {
        await api.put(`/departments/${editingDept.id}`, values);
        message.success('Подразделение обновлено');
      } else {
        await api.post('/departments', values);
        message.success('Подразделение добавлено');
      }
      setModalOpen(false);
      fetchDepartments();
    } catch (err: any) {
      if (err.response?.data?.detail) message.error(err.response.data.detail);
    }
  };

  const handleDelete = async (dept: Department) => {
    if (dept.employee_count > 0) {
      // Show move modal
      setMoveDept(dept);
      setMoveLoading(true);
      setMoveModalOpen(true);
      try {
        const res = await api.get(`/departments/${dept.id}/employees`);
        setMoveEmployees(res.data.map((e: DeptEmployee) => ({ ...e, newDepartmentId: undefined })));
      } catch {
        setMoveEmployees([]);
      } finally {
        setMoveLoading(false);
      }
    } else {
      // Direct delete with confirmation
      confirm({
        title: 'Удалить подразделение?',
        icon: <ExclamationCircleOutlined />,
        content: `Подразделение "${dept.name}" будет удалено.`,
        okText: 'Удалить',
        okType: 'danger',
        cancelText: 'Отмена',
        onOk: async () => {
          try {
            await api.delete(`/departments/${dept.id}`);
            message.success('Подразделение удалено');
            fetchDepartments();
          } catch (err: any) {
            message.error(err.response?.data?.detail || 'Ошибка удаления');
          }
        },
      });
    }
  };

  const handleMoveAndDelete = async () => {
    if (!moveDept) return;

    const allAssigned = moveEmployees.every(e => e.newDepartmentId);
    if (!allAssigned) {
      message.warning('Выберите подразделение для всех сотрудников');
      return;
    }

    try {
      const moves = moveEmployees.map(e => ({
        employee_id: e.id,
        department_id: e.newDepartmentId,
      }));
      await api.post(`/departments/${moveDept.id}/move-employees`, { moves });

      await api.delete(`/departments/${moveDept.id}`);
      message.success('Сотрудники перемещены, подразделение удалено');
      setMoveModalOpen(false);
      fetchDepartments();
    } catch (err: any) {
      message.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const updateMoveEmployee = (empId: number, newDeptId: number) => {
    setMoveEmployees(prev =>
      prev.map(e => e.id === empId ? { ...e, newDepartmentId: newDeptId } : e)
    );
  };

  const otherDepartments = departments.filter(d => d.id !== moveDept?.id);

  const columns = [
    { title: 'Название', dataIndex: 'name', key: 'name' },
    { title: 'Порядок', dataIndex: 'sort_order', key: 'sort_order', width: 80 },
    {
      title: 'Сотрудники',
      dataIndex: 'employee_count',
      key: 'employee_count',
      width: 110,
      render: (v: number) => v > 0 ? <Tag color="blue">{v}</Tag> : <Tag>0</Tag>,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (_: any, rec: Department) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(rec)} />
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(rec)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={5} style={{ margin: 0 }}>Подразделения</Title></Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchDepartments} />
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              Добавить
            </Button>
          </Space>
        </Col>
      </Row>

      <Table dataSource={departments} columns={columns} rowKey="id" loading={loading}
        pagination={false} size="middle" />

      {/* Create/Edit modal (for empty departments) */}
      <Modal
        title={editingDept ? 'Редактирование подразделения' : 'Новое подразделение'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="sort_order" label="Порядок сортировки">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Rename modal (for departments with employees) */}
      <Modal
        title={`Переименование подразделения "${renameDept?.name}"`}
        open={renameModalOpen}
        onOk={handleRenameConfirm}
        onCancel={() => setRenameModalOpen(false)}
        okText="Переименовать"
        cancelText="Отмена"
        width={600}
      >
        <Form layout="vertical">
          <Form.Item label="Новое название">
            <Input value={newName} onChange={e => setNewName(e.target.value)} />
          </Form.Item>
        </Form>

        {renameEmployees.length > 0 && (
          <>
            <Text type="warning" style={{ display: 'block', marginBottom: 8 }}>
              В подразделении находятся сотрудники ({renameEmployees.length}).
              Оно будет переименовано в "{newName}".
            </Text>
            <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}>
              <List
                loading={renameLoading}
                size="small"
                dataSource={renameEmployees}
                renderItem={(emp) => (
                  <List.Item>
                    <Text>{emp.last_name} {emp.first_name} {emp.middle_name || ''}</Text>
                    {emp.birth_date && (
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        {new Date(emp.birth_date).toLocaleDateString('ru-RU')}
                      </Text>
                    )}
                  </List.Item>
                )}
              />
            </div>
          </>
        )}
      </Modal>

      {/* Move employees modal (before delete) */}
      <Modal
        title={`Удаление подразделения "${moveDept?.name}"`}
        open={moveModalOpen}
        onCancel={() => setMoveModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setMoveModalOpen(false)}>Отмена</Button>,
          <Button key="move" type="primary" danger icon={<SwapOutlined />} onClick={handleMoveAndDelete}>
            Переместить и удалить
          </Button>,
        ]}
        width={700}
      >
        <Text type="warning" style={{ display: 'block', marginBottom: 16 }}>
          В подразделении "{moveDept?.name}" имеются сотрудники ({moveEmployees.length}).
          Прежде чем удалить, переместите сотрудников в другие подразделения.
        </Text>

        <div style={{ maxHeight: 400, overflow: 'auto' }}>
          <Table
            loading={moveLoading}
            dataSource={moveEmployees}
            rowKey="id"
            pagination={false}
            size="small"
            columns={[
              {
                title: 'ФИО',
                key: 'fio',
                render: (_: any, r: any) => `${r.last_name} ${r.first_name} ${r.middle_name || ''}`,
              },
              {
                title: 'Дата рождения',
                dataIndex: 'birth_date',
                key: 'birth_date',
                width: 120,
                render: (v: string) => v ? new Date(v).toLocaleDateString('ru-RU') : '—',
              },
              {
                title: 'Новое подразделение',
                key: 'new_dept',
                width: 220,
                render: (_: any, rec: any) => (
                  <Select
                    placeholder="Выберите"
                    style={{ width: '100%' }}
                    size="small"
                    value={rec.newDepartmentId}
                    onChange={(v) => updateMoveEmployee(rec.id, v)}
                  >
                    {otherDepartments.map(d => (
                      <Option key={d.id} value={d.id}>{d.name}</Option>
                    ))}
                  </Select>
                ),
              },
            ]}
          />
        </div>
      </Modal>
    </>
  );
};

// ==================== POSITIONS TAB ====================

const PositionsTab: React.FC = () => {
  const [positions, setPositions] = useState<Position[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPos, setEditingPos] = useState<Position | null>(null);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [posRes, deptRes] = await Promise.all([
        api.get('/positions'),
        api.get('/departments'),
      ]);
      setPositions(posRes.data);
      setDepartments(deptRes.data);
    } catch {
      message.error('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreateModal = () => {
    setEditingPos(null);
    form.resetFields();
    form.setFieldValue('sort_order', positions.length + 1);
    setModalOpen(true);
  };

  const openEditModal = (pos: Position) => {
    setEditingPos(pos);
    form.setFieldsValue({
      name: pos.name,
      sort_order: pos.sort_order,
      department_ids: pos.department_ids,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingPos) {
        await api.put(`/positions/${editingPos.id}`, values);
        message.success('Должность обновлена');
      } else {
        await api.post('/positions', values);
        message.success('Должность добавлена');
      }
      setModalOpen(false);
      fetchData();
    } catch (err: any) {
      if (err.response?.data?.detail) message.error(err.response.data.detail);
    }
  };

  const handleDelete = (pos: Position) => {
    if (pos.employee_count > 0) {
      Modal.warning({
        title: 'Невозможно удалить',
        content: `На должности "${pos.name}" есть сотрудники (${pos.employee_count}). Сначала измените должность у сотрудников.`,
      });
      return;
    }

    confirm({
      title: 'Удалить должность?',
      icon: <ExclamationCircleOutlined />,
      content: `Должность "${pos.name}" будет удалена.`,
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await api.delete(`/positions/${pos.id}`);
          message.success('Должность удалена');
          fetchData();
        } catch (err: any) {
          message.error(err.response?.data?.detail || 'Ошибка');
        }
      },
    });
  };

  const columns = [
    { title: 'Название', dataIndex: 'name', key: 'name' },
    {
      title: 'Подразделения',
      key: 'departments',
      render: (_: any, r: Position) =>
        r.department_names.length > 0
          ? r.department_names.map(n => <Tag key={n}>{n}</Tag>)
          : <Text type="secondary">—</Text>,
    },
    { title: 'Порядок', dataIndex: 'sort_order', key: 'sort_order', width: 80 },
    {
      title: 'Сотрудники',
      dataIndex: 'employee_count',
      key: 'employee_count',
      width: 110,
      render: (v: number) => v > 0 ? <Tag color="blue">{v}</Tag> : <Tag>0</Tag>,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (_: any, rec: Position) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditModal(rec)} />
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(rec)} />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={5} style={{ margin: 0 }}>Должности</Title></Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData} />
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              Добавить
            </Button>
          </Space>
        </Col>
      </Row>

      <Table dataSource={positions} columns={columns} rowKey="id" loading={loading}
        pagination={false} size="middle" />

      <Modal
        title={editingPos ? 'Редактирование должности' : 'Новая должность'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="department_ids" label="Подразделения">
            <Select mode="multiple" placeholder="Выберите подразделения" allowClear>
              {departments.map(d => (
                <Option key={d.id} value={d.id}>{d.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="sort_order" label="Порядок сортировки">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ==================== MAIN SETTINGS PAGE ====================

const Settings: React.FC = () => {
  return (
    <div>
      <Title level={4}>Настройки</Title>
      <Card>
        <Tabs
          defaultActiveKey="departments"
          items={[
            { key: 'departments', label: 'Подразделения', children: <DepartmentsTab /> },
            { key: 'positions', label: 'Должности', children: <PositionsTab /> },
          ]}
        />
      </Card>
    </div>
  );
};

export default Settings;
